import React, { useEffect, useState } from 'react';
import { Download, Printer } from 'lucide-react';
import api from '../services/api';

// Helper to format month from date range
const getMonthLabel = (dateRange) => {
  if (!dateRange?.start) return '';
  const date = new Date(dateRange.start);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

// Helper to format date range
const formatDateRange = (dateRange) => {
  if (!dateRange?.start || !dateRange?.end) return '';
  const s = new Date(dateRange.start);
  const e = new Date(dateRange.end);
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${s.toLocaleDateString('en-KE', opts)} - ${e.toLocaleDateString('en-KE', opts)}`;
};

const PrintableReport = ({ 
  title, 
  dateRange, 
  children, 
  onExportCsv,
  onPrint,
  showCompanyHeader = true 
}) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        setSettings(res.data?.settings || {});
      } catch (e) {
        // Use defaults if settings fail
        setSettings({
          clinic_name: 'NutriAxis Nutrition Consultancy & Wellness Clinic',
          clinic_address: 'Nairobi, Kenya',
          clinic_phone: '+254 700 000000',
          clinic_email: 'info@nutriaxis.com'
        });
      }
    };
    fetchSettings();
  }, []);

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const handleDownload = () => {
    if (onExportCsv) {
      onExportCsv();
    }
  };

  const companyName = settings?.clinic_name || 'NutriAxis Nutrition Consultancy & Wellness Clinic';
  const companyAddress = settings?.clinic_address || 'Nairobi, Kenya';
  const companyPhone = settings?.clinic_phone || '+254 700 000000';
  const companyEmail = settings?.clinic_email || 'info@nutriaxis.com';

  return (
    <div className="print-report-shell bg-white">
      {/* Print Header - Company Details */}
      {showCompanyHeader && (
        <div className="print-header border-b border-gray-200 pb-4 mb-6">
          <div className="print-header-grid grid grid-cols-3 items-start gap-4">
            {/* Company Logo/Name */}
            <div className="print-logo-wrap">
              <div className="w-20 h-20 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                <span className="text-blue-700 font-bold text-lg">NA</span>
              </div>
              <div>
                <h1 className="print-company-name text-lg font-bold text-gray-900">
                  {companyName}
                </h1>
                <p className="print-company-address text-sm text-gray-600">
                  {companyAddress}
                </p>
                <p className="print-company-contact text-sm text-gray-600">
                  {companyPhone} | {companyEmail}
                </p>
              </div>
            </div>

            {/* Report Title */}
            <div className="text-center">
              <h2 className="print-report-title text-xl font-bold text-blue-900 uppercase">
                {companyName} {title}
              </h2>
              <p className="print-report-period text-sm text-gray-600 mt-1">
                As per {getMonthLabel(dateRange) ? `The Month Of ${getMonthLabel(dateRange)}` : 'Selected Period'}
              </p>
            </div>

            {/* Generated Info */}
            <div className="print-header-right text-right text-sm">
              <p className="print-generated-at">
                <span className="font-semibold">Generated:</span> {new Date().toLocaleDateString('en-KE', { 
                  day: '2-digit', 
                  month: 'short', 
                  year: 'numeric' 
                })}
              </p>
              <p className="print-prepared-by">
                <span className="font-semibold">Prepared by:</span> System
              </p>
              {dateRange && (
                <p className="print-report-period">
                  <span className="font-semibold">Period:</span> {formatDateRange(dateRange)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons - Hidden in print */}
      <div className="no-print flex items-center gap-3 mb-4">
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Download size={16} />
          Download Report
        </button>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Printer size={16} />
          Print Report
        </button>
      </div>

      {/* Report Content */}
      <div className="print-main">
        {children}
      </div>

      {/* Print Footer */}
      <div className="print-footer no-print mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500">
        <div className="print-footer-grid grid grid-cols-3">
          <div>
            <span className="font-semibold">NutriAxis Clinic</span>
            <span>Comprehensive Health & Nutrition Solutions</span>
          </div>
          <div className="text-center">
            <span>This report was generated automatically by the system</span>
          </div>
          <div className="print-page text-right">
            <span>Page 1</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableReport;